import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, AlertCircle, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SkeletonRows } from "@/components/app/Common";
import { get } from "@/lib/api";
import { download } from "@/lib/fit";

function MonthlyReport() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  return (
    <section className="panel flex flex-wrap items-end gap-3 p-6" data-testid="dpdp-report-card">
      <div className="min-w-0 flex-1"><h2 className="text-base font-semibold">DPO monthly report</h2>
        <p className="text-sm text-ink-secondary">Consents given and withdrawn, rights requests and SLA, erasures, retention deletions, incidents and the storage purge backlog.</p></div>
      <label className="space-y-1 text-sm"><span className="block text-ink-secondary">Month</span>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-lg border border-line bg-white px-2" data-testid="dpdp-report-month" /></label>
      <Button size="sm" disabled={!month} onClick={() => download(`/settings/dpdp/report?month=${month}&format=pdf`, `dpdp-report-${month}.pdf`)} data-testid="dpdp-report-pdf-btn"><FileDown size={14} /> PDF</Button>
      <Button size="sm" variant="outline" disabled={!month} onClick={() => download(`/settings/dpdp/report?month=${month}&format=csv`, `dpdp-report-${month}.csv`)} data-testid="dpdp-report-csv-btn">CSV</Button>
    </section>
  );
}

export default function DpdpChecklist() {
  const { data, isLoading } = useQuery({ queryKey: ["dpdp-checklist"], queryFn: () => get("/settings/dpdp/checklist") });
  if (isLoading) return <SkeletonRows rows={8} />;
  return (
    <div className="space-y-6" data-testid="dpdp-checklist-page">
      <MonthlyReport />
      <section className="panel overflow-hidden">
        <div className="border-b border-line px-6 py-4"><h2 className="text-lg font-semibold">DPDP compliance</h2>
          <p className="text-sm text-ink-secondary" data-testid="dpdp-summary">{data.summary.implemented} in place · {data.summary.needs_action} need action. Digital Personal Data Protection Act 2023 and DPDP Rules 2025.</p></div>
        <ul className="divide-y divide-line">
          {data.items.map((i, n) => (
            <li key={n} className="flex gap-3 px-6 py-3 text-sm" data-testid={`dpdp-item-${n}`}>
              {i.status === "implemented" ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-brand" aria-label="Implemented" /> : <AlertCircle size={16} className="mt-0.5 shrink-0 text-caution" aria-label="Needs action" />}
              <div className="min-w-0 flex-1"><p><span className="mr-2 text-xs uppercase tracking-wide text-ink-muted">{i.area}</span>{i.title}</p>{i.detail && <p className="text-xs text-ink-muted">{i.detail}</p>}</div>
              <span className={`h-fit shrink-0 rounded-full px-2 py-0.5 text-xs ${i.status === "implemented" ? "bg-brand-tint text-brand" : "bg-caution-tint text-caution"}`}>{i.status === "implemented" ? "Implemented" : "Needs action by firm"}</span>
            </li>
          ))}
        </ul>
      </section>
      <section className="panel space-y-2 p-6" data-testid="dpdp-firm-actions">
        <h2 className="text-base font-semibold">What your firm must do outside the app</h2>
        <ol className="list-decimal space-y-1 pl-5 text-sm text-ink-secondary">{data.firm_actions.map((a) => <li key={a}>{a}</li>)}</ol>
      </section>
      <section className="panel overflow-x-auto p-6" data-testid="dpdp-vendors">
        <h2 className="mb-2 text-base font-semibold">Sub-processors and cross-border transfers</h2>
        <table className="w-full min-w-[560px] text-left text-sm"><thead className="text-xs text-ink-muted"><tr><th className="py-1">Vendor</th><th>Used for</th><th>Action</th></tr></thead>
          <tbody className="divide-y divide-line">{data.vendors.map((v) => <tr key={v.vendor}><td className="py-2 pr-3 font-medium">{v.vendor}</td><td className="pr-3 text-ink-secondary">{v.use}</td><td className="text-ink-secondary">{v.action}</td></tr>)}</tbody></table>
      </section>
    </div>
  );
}
